import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import { getOpenAIStatus, manualQuotaReset } from '../../services/openaiService';

const OpenAIStatusAlert = ({ onRetry, className = '' }) => {
  const [status, setStatus] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = () => {
    const currentStatus = getOpenAIStatus();
    setStatus(currentStatus);
  };

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      // Reset quota state if needed
      if (status?.quotaExceeded) {
        manualQuotaReset();
      }
      
      // Wait a moment before retrying
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (onRetry) {
        await onRetry();
      }
      
      // Refresh status
      loadStatus();
    } catch (error) {
      console.error('Retry failed:', error);
    } finally {
      setIsRetrying(false);
    }
  };

  const handleFixApiKey = () => {
    window.open('https://platform.openai.com/account/api-keys', '_blank');
  };

  const handleCheckBilling = () => {
    window.open('https://platform.openai.com/account/billing', '_blank');
  };

  const handleCheckStatus = () => {
    window.open('https://status.openai.com', '_blank');
  };

  if (!status || status?.status === 'available') {
    return null;
  }

  const getAlertConfig = () => {
    switch (status?.status) {
      case 'not_configured':
        return {
          type: 'error',
          icon: 'AlertCircle',
          title: 'OpenAI API Not Configured',
          message: status?.apiKeyValidation?.message || 'OpenAI API key is required for AI-powered features',
          bgColor: 'bg-error/10',
          borderColor: 'border-error/20',
          textColor: 'text-error',
          actions: ['fix_api_key', 'details']
        };
      case 'quota_exceeded':
        return {
          type: 'warning',
          icon: 'AlertTriangle',
          title: 'OpenAI Quota Exceeded',
          message: 'Your OpenAI usage quota has been exceeded. AI features are temporarily disabled.',
          bgColor: 'bg-warning/10',
          borderColor: 'border-warning/20',
          textColor: 'text-warning',
          actions: ['billing', 'retry', 'details']
        };
      default:
        return {
          type: 'warning',
          icon: 'AlertTriangle',
          title: 'AI Services Unavailable',
          message: 'AI-powered features are temporarily unavailable',
          bgColor: 'bg-warning/10',
          borderColor: 'border-warning/20',
          textColor: 'text-warning',
          actions: ['retry', 'details']
        };
    }
  };

  const config = getAlertConfig();

  return (
    <div className={`${config?.bgColor} ${config?.borderColor} border rounded-lg p-4 ${className}`}>
      <div className="flex items-start space-x-3">
        <div className={`flex-shrink-0 w-5 h-5 ${config?.textColor}`}>
          <Icon name={config?.icon} size={20} />
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className={`text-sm font-medium ${config?.textColor}`}>
            {config?.title}
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            {config?.message}
          </p>

          {/* Enhanced Error Details */}
          {showDetails && status?.apiKeyValidation && (
            <div className="mt-3 p-3 bg-card border border-border rounded-md">
              <h4 className="text-xs font-medium text-foreground mb-2">Diagnostic Information:</h4>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>• Error Type: {status?.apiKeyValidation?.error || 'Unknown'}</li>
                <li>• Status: {status?.status}</li>
                {status?.consecutiveErrors > 0 && (
                  <li>• Consecutive Errors: {status?.consecutiveErrors}</li>
                )}
                {status?.cooldownEndsAt && (
                  <li>• Cooldown Until: {new Date(status?.cooldownEndsAt)?.toLocaleTimeString()}</li>
                )}
              </ul>
              
              {/* Quick Fix Suggestions */}
              {status?.recommendations?.length > 0 && (
                <div className="mt-3">
                  <h5 className="text-xs font-medium text-foreground mb-1">Quick Fixes:</h5>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    {status?.recommendations?.slice(0, 3)?.map((rec, index) => (
                      <li key={index}>• {rec?.action || rec?.description}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Recovery Instructions */}
          {showDetails && status?.status === 'not_configured' && (
            <div className="mt-3 p-3 bg-muted/30 rounded-md">
              <h4 className="text-xs font-medium text-foreground mb-2">Setup Instructions:</h4>
              <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
                <li>Visit <a href="https://platform.openai.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">platform.openai.com</a></li>
                <li>Create account or sign in</li>
                <li>Go to API Keys section</li>
                <li>Generate new API key</li>
                <li>Add to .env file as VITE_OPENAI_API_KEY</li>
                <li>Restart your development server</li>
              </ol>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center space-x-2">
          {config?.actions?.includes('fix_api_key') && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleFixApiKey}
              className="text-xs"
            >
              <Icon name="ExternalLink" size={14} className="mr-1" />
              Fix API Key
            </Button>
          )}
          
          {config?.actions?.includes('billing') && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleCheckBilling}
              className="text-xs"
            >
              <Icon name="CreditCard" size={14} className="mr-1" />
              Check Billing
            </Button>
          )}

          {config?.actions?.includes('retry') && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleRetry}
              disabled={isRetrying}
              className="text-xs"
            >
              {isRetrying ? (
                <Icon name="Loader2" size={14} className="mr-1 animate-spin" />
              ) : (
                <Icon name="RefreshCw" size={14} className="mr-1" />
              )}
              {isRetrying ? 'Retrying...' : 'Retry'}
            </Button>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={handleCheckStatus}
            className="text-xs"
          >
            <Icon name="ExternalLink" size={14} className="mr-1" />
            Service Status
          </Button>
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowDetails(!showDetails)}
          className="text-xs"
        >
          <Icon name={showDetails ? "ChevronUp" : "ChevronDown"} size={14} className="mr-1" />
          {showDetails ? 'Less' : 'Details'}
        </Button>
      </div>
    </div>
  );
};

export default OpenAIStatusAlert;